import { useEffect, useMemo, useState } from 'react';
import { useSearchParams } from 'react-router-dom';
import {
  Alert,
  Box,
  Button,
  Chip,
  CircularProgress,
  Divider,
  FormControl,
  IconButton,
  InputLabel,
  MenuItem,
  Paper,
  Select,
  Stack,
  TextField,
  Typography,
} from '@mui/material';
import RefreshIcon from '@mui/icons-material/Refresh';
import {
  useNotifications,
  useNotificationTypes,
} from '../api/useNotifications';
import { useNotificationsSocket } from '../hooks/useNotificationsSocket';
import {
  NotificationEvent,
  NotificationEventType,
  NotificationsFilter,
} from '../types/notifications.types';

const PRESET_RANGES: Array<{ label: string; minutes: number | null }> = [
  { label: 'All time', minutes: null },
  { label: 'Last 15m', minutes: 15 },
  { label: 'Last 1h', minutes: 60 },
  { label: 'Last 6h', minutes: 360 },
  { label: 'Last 24h', minutes: 60 * 24 },
];

const SEVERITY_COLOR: Record<
  NotificationEvent['severity'],
  'default' | 'info' | 'warning' | 'error'
> = {
  info: 'info',
  warning: 'warning',
  error: 'error',
};

function severityChipColor(s: NotificationEvent['severity']) {
  return SEVERITY_COLOR[s] || 'default';
}

function formatTime(iso: string): string {
  try {
    return new Date(iso).toLocaleString();
  } catch {
    return iso;
  }
}

function readFilterFromParams(params: URLSearchParams): NotificationsFilter {
  const types = params.getAll('type') as NotificationEventType[];
  return {
    from: params.get('from') ?? undefined,
    to: params.get('to') ?? undefined,
    ruleId: params.get('ruleId') ?? undefined,
    pairId: params.get('pairId') ?? undefined,
    types: types.length > 0 ? types : undefined,
    platform: (params.get('platform') as NotificationsFilter['platform']) ?? 'all',
    source: (params.get('source') as NotificationsFilter['source']) ?? 'all',
    severity:
      (params.get('severity') as NotificationsFilter['severity']) ?? 'all',
  };
}

function writeFilterToParams(filter: NotificationsFilter): URLSearchParams {
  const p = new URLSearchParams();
  if (filter.from) p.set('from', filter.from);
  if (filter.to) p.set('to', filter.to);
  if (filter.ruleId) p.set('ruleId', filter.ruleId);
  if (filter.pairId) p.set('pairId', filter.pairId);
  if (filter.types) for (const t of filter.types) p.append('type', t);
  if (filter.platform && filter.platform !== 'all')
    p.set('platform', filter.platform);
  if (filter.source && filter.source !== 'all') p.set('source', filter.source);
  if (filter.severity && filter.severity !== 'all')
    p.set('severity', filter.severity);
  return p;
}

/** Pulls the most operator-relevant fields out of the type-specific
 *  payload for a quick at-a-glance line on each row. The row also
 *  shows raw JSON in its expanded view. */
function summarizeEvent(evt: NotificationEvent): string {
  const p = (evt.payload || {}) as any;
  switch (evt.type) {
    case 'maker_placed':
    case 'maker_filled':
    case 'maker_partial_fill':
      return `${evt.platform ?? '?'} · ${p.pairName ?? evt.pairName ?? ''} · qty=${p.qty ?? p.filledQty ?? '?'} @ $${p.price ?? p.filledPrice ?? '?'}`;
    case 'maker_adjusted':
      return `${evt.platform ?? '?'} · ${p.pairName ?? evt.pairName ?? ''} · $${p.oldPrice}→$${p.newPrice}`;
    case 'maker_cancelled':
      return `${evt.platform ?? '?'} · ${p.pairName ?? evt.pairName ?? ''} · ${p.reason ?? ''}`;
    case 'manual_order_placed':
    case 'manual_order_executed':
      return `✋ ${evt.platform ?? '?'} · ${p.marketTitle ?? ''} · ${p.action?.toUpperCase?.() ?? ''} ${p.qty ?? p.filledQty ?? ''} @ $${p.price ?? '?'}`;
    case 'execution_complete':
      return `${p.pairName ?? evt.pairName ?? ''} · TNA ${p.effectiveTna?.toFixed?.(2) ?? '?'}% · capital $${p.capitalRecovered ?? '?'}`;
    case 'error':
      return `${evt.pairName ?? evt.ruleId ?? ''} · ${p.state ?? ''} · ${p.errorMessage ?? ''}`;
    case 'tna_negative':
      return `${evt.pairName ?? ''} · taker TNA ${p.takerTna?.toFixed?.(2) ?? '?'}%`;
    case 'net_contracts_digest':
      return `digest threshold $${p.threshold ?? '?'} · ${p.top?.length ?? 0} pairs`;
    case 'platform_disconnect':
      return `${p.platform} · cancelled ${p.cancelledCount ?? 0}`;
    case 'platform_reconnect':
      return `${p.platform} reconnected`;
    case 'counter_skipped':
      return `${evt.pairName ?? ''} · ${p.reason ?? ''} (rawBid ${p.rawBid ?? '?'}, netBid ${p.netBid ?? '?'})`;
    default:
      return JSON.stringify(p).slice(0, 120);
  }
}

function NotificationRow({ evt }: { evt: NotificationEvent }) {
  const [open, setOpen] = useState(false);
  return (
    <Paper
      variant="outlined"
      sx={{ p: 1.5, mb: 1, borderLeft: 3, borderLeftColor: `${severityChipColor(evt.severity)}.main` }}
    >
      <Stack
        direction="row"
        spacing={1.5}
        alignItems="center"
        onClick={() => setOpen((o) => !o)}
        sx={{ cursor: 'pointer' }}
      >
        <Chip
          size="small"
          label={evt.type}
          color={severityChipColor(evt.severity)}
          variant="outlined"
        />
        <Chip
          size="small"
          label={evt.source}
          variant="outlined"
          sx={{ textTransform: 'capitalize' }}
        />
        <Typography variant="body2" sx={{ flex: 1 }}>
          {summarizeEvent(evt)}
        </Typography>
        <Typography variant="caption" color="text.secondary">
          {formatTime(evt.occurredAt)}
        </Typography>
      </Stack>
      {open && (
        <Box
          component="pre"
          sx={{
            mt: 1.5,
            p: 1.5,
            bgcolor: 'background.default',
            borderRadius: 1,
            fontSize: 12,
            overflowX: 'auto',
            margin: 0,
          }}
        >
          {JSON.stringify(evt, null, 2)}
        </Box>
      )}
    </Paper>
  );
}

export function NotificationsPage() {
  const [searchParams, setSearchParams] = useSearchParams();
  const [filter, setFilter] = useState<NotificationsFilter>(() =>
    readFilterFromParams(searchParams),
  );
  const [livePrepended, setLivePrepended] = useState<NotificationEvent[]>([]);

  const { data: typesData } = useNotificationTypes();
  const allTypes = typesData?.types ?? [];

  const {
    data,
    fetchNextPage,
    hasNextPage,
    isFetching,
    isFetchingNextPage,
    isError,
    refetch,
  } = useNotifications(filter);

  // Persist filter in URL.
  useEffect(() => {
    const next = writeFilterToParams(filter);
    setSearchParams(next, { replace: true });
    // Reset live overlay when filters change — old events may not
    // match the new query.
    setLivePrepended([]);
  }, [filter, setSearchParams]);

  // Live append from WS.
  const { latestEvent, acknowledge, isConnected } = useNotificationsSocket();
  useEffect(() => {
    if (!latestEvent) return;
    if (matchesFilter(latestEvent, filter)) {
      setLivePrepended((rows) => [latestEvent, ...rows]);
    }
    acknowledge();
  }, [latestEvent, filter, acknowledge]);

  // Combined feed: live events on top, then react-query pages, deduped by id.
  const rows = useMemo<NotificationEvent[]>(() => {
    const seen = new Set<string>();
    const out: NotificationEvent[] = [];
    for (const e of livePrepended) {
      if (!seen.has(e.id)) {
        seen.add(e.id);
        out.push(e);
      }
    }
    if (data) {
      for (const page of data.pages) {
        for (const e of page.items) {
          if (!seen.has(e.id)) {
            seen.add(e.id);
            out.push(e);
          }
        }
      }
    }
    return out;
  }, [livePrepended, data]);

  return (
    <Box sx={{ p: 3 }}>
      <Stack direction="row" alignItems="center" spacing={2} sx={{ mb: 2 }}>
        <Typography variant="h5">Notifications</Typography>
        <Chip
          size="small"
          color={isConnected ? 'success' : 'default'}
          label={isConnected ? 'live' : 'disconnected'}
        />
        <Box sx={{ flex: 1 }} />
        <IconButton size="small" onClick={() => refetch()} title="Refresh">
          <RefreshIcon fontSize="small" />
        </IconButton>
      </Stack>

      <Paper variant="outlined" sx={{ p: 2, mb: 2 }}>
        <Stack
          direction={{ xs: 'column', md: 'row' }}
          spacing={2}
          alignItems={{ md: 'flex-end' }}
          flexWrap="wrap"
        >
          <Box>
            <Typography variant="caption" color="text.secondary">
              Time range
            </Typography>
            <Stack direction="row" spacing={1} sx={{ mt: 0.5 }}>
              {PRESET_RANGES.map((r) => (
                <Chip
                  key={r.label}
                  label={r.label}
                  size="small"
                  variant="outlined"
                  onClick={() =>
                    setFilter((f) => ({
                      ...f,
                      from:
                        r.minutes == null
                          ? undefined
                          : new Date(
                              Date.now() - r.minutes * 60_000,
                            ).toISOString(),
                      to: undefined,
                    }))
                  }
                />
              ))}
            </Stack>
          </Box>

          <TextField
            label="Pair Id"
            size="small"
            value={filter.pairId ?? ''}
            onChange={(e) =>
              setFilter((f) => ({ ...f, pairId: e.target.value || undefined }))
            }
          />
          <TextField
            label="Rule Id"
            size="small"
            value={filter.ruleId ?? ''}
            onChange={(e) =>
              setFilter((f) => ({ ...f, ruleId: e.target.value || undefined }))
            }
          />

          <FormControl size="small" sx={{ minWidth: 240 }}>
            <InputLabel id="type-filter">Type</InputLabel>
            <Select
              multiple
              labelId="type-filter"
              label="Type"
              value={filter.types ?? []}
              onChange={(e) =>
                setFilter((f) => ({
                  ...f,
                  types:
                    typeof e.target.value === 'string'
                      ? (e.target.value
                          .split(',')
                          .filter(Boolean) as NotificationEventType[])
                      : (e.target.value as NotificationEventType[]),
                }))
              }
              renderValue={(sel) =>
                (sel as string[]).length > 0
                  ? (sel as string[]).join(', ')
                  : 'Any'
              }
            >
              {allTypes.map((t) => (
                <MenuItem key={t} value={t}>
                  {t}
                </MenuItem>
              ))}
            </Select>
          </FormControl>

          <FormControl size="small" sx={{ minWidth: 140 }}>
            <InputLabel id="platform-filter">Platform</InputLabel>
            <Select
              labelId="platform-filter"
              label="Platform"
              value={filter.platform ?? 'all'}
              onChange={(e) =>
                setFilter((f) => ({
                  ...f,
                  platform: e.target.value as any,
                }))
              }
            >
              <MenuItem value="all">All</MenuItem>
              <MenuItem value="kalshi">Kalshi</MenuItem>
              <MenuItem value="polymarket">Polymarket</MenuItem>
            </Select>
          </FormControl>

          <FormControl size="small" sx={{ minWidth: 140 }}>
            <InputLabel id="source-filter">Source</InputLabel>
            <Select
              labelId="source-filter"
              label="Source"
              value={filter.source ?? 'all'}
              onChange={(e) =>
                setFilter((f) => ({ ...f, source: e.target.value as any }))
              }
            >
              <MenuItem value="all">All</MenuItem>
              <MenuItem value="automation">Automation</MenuItem>
              <MenuItem value="manual">Manual ✋</MenuItem>
              <MenuItem value="system">System</MenuItem>
            </Select>
          </FormControl>

          <FormControl size="small" sx={{ minWidth: 140 }}>
            <InputLabel id="severity-filter">Severity</InputLabel>
            <Select
              labelId="severity-filter"
              label="Severity"
              value={filter.severity ?? 'all'}
              onChange={(e) =>
                setFilter((f) => ({
                  ...f,
                  severity: e.target.value as any,
                }))
              }
            >
              <MenuItem value="all">All</MenuItem>
              <MenuItem value="info">Info</MenuItem>
              <MenuItem value="warning">Warning</MenuItem>
              <MenuItem value="error">Error</MenuItem>
            </Select>
          </FormControl>

          <Button
            size="small"
            onClick={() =>
              setFilter({ platform: 'all', source: 'all', severity: 'all' })
            }
          >
            Clear
          </Button>
        </Stack>
      </Paper>

      {isError && (
        <Alert severity="error" sx={{ mb: 2 }}>
          Failed to load notifications.
        </Alert>
      )}

      {isFetching && rows.length === 0 ? (
        <Stack direction="row" alignItems="center" spacing={1}>
          <CircularProgress size={18} />
          <Typography variant="body2">Loading…</Typography>
        </Stack>
      ) : rows.length === 0 ? (
        <Typography variant="body2" color="text.secondary">
          No notifications match the current filters.
        </Typography>
      ) : (
        <>
          {rows.map((evt) => (
            <NotificationRow key={evt.id} evt={evt} />
          ))}
          <Divider sx={{ my: 2 }} />
          <Stack direction="row" justifyContent="center">
            {hasNextPage ? (
              <Button
                onClick={() => fetchNextPage()}
                disabled={isFetchingNextPage}
              >
                {isFetchingNextPage ? 'Loading…' : 'Load older'}
              </Button>
            ) : (
              <Typography variant="caption" color="text.secondary">
                End of feed.
              </Typography>
            )}
          </Stack>
        </>
      )}
    </Box>
  );
}

function matchesFilter(
  evt: NotificationEvent,
  filter: NotificationsFilter,
): boolean {
  if (filter.types && filter.types.length > 0 && !filter.types.includes(evt.type))
    return false;
  if (
    filter.platform &&
    filter.platform !== 'all' &&
    evt.platform !== filter.platform
  )
    return false;
  if (filter.source && filter.source !== 'all' && evt.source !== filter.source)
    return false;
  if (
    filter.severity &&
    filter.severity !== 'all' &&
    evt.severity !== filter.severity
  )
    return false;
  if (filter.ruleId && evt.ruleId !== filter.ruleId) return false;
  if (filter.pairId && evt.pairId !== filter.pairId) return false;
  if (filter.from) {
    const t = new Date(filter.from).getTime();
    if (new Date(evt.occurredAt).getTime() < t) return false;
  }
  return true;
}
